import settings from './settings'
import validator from './validator'
import errors from './errors'

import eventbus from './eventbus'
import schemas from './schemas'

import pageProps from './page-props'
import idmanager from './idmanager'

const horizonClientState = {
  READY: 'stateReady',
  UNLOADED: 'stateNotReady',
  LOADING: 'stateLoading',
}

const supportedEnvs = ['web', 'instant-article', 'app']

/**
 * The globocom-horizon is a JavaScript library for capturing and
 * validating user events. See {@tutorial click} for an overview.
 */
class HorizonClient {

  // static useQAConfiguration () {
  //   settings.useQAConfiguration()
  // }
  /**
   * Creates an instance of {@link HorizonClient}.
   *
   * @param {string} tenant Client group identifier.
   * @param {string} deviceGroup The device group where the client is being executed.
   * @param {string} environment The environment where the client is being executed.
   */
  constructor(tenant = null, deviceGroup = null, defaultContentType = null, environment = 'web') {
    this.tenant = tenant || pageProps.getTenant() || errors.mustBeDefined('tenant')
    this.deviceGroup =
      deviceGroup
      || pageProps.getDeviceGroup()
      || errors.mustBeDefined('deviceGroup')

    this.defaultContentType = defaultContentType

    if (supportedEnvs.indexOf(environment) !== -1) this.environment = environment
    else throw Error(errors.INVALID_ENVIRONMENT)

    this.validator = validator
    this.clientVersion = settings.PACKAGE_VERSION
    this.state = horizonClientState.UNLOADED
    this.referer = document.referrer

    // private variables
    let schemasProvider = null
    let eventBus = null
    let idManager = null

    this.setSchemasProvider = (provider) => { schemasProvider = provider }

    this.getSchemasProvider = () => {
      if (!schemasProvider) {
        const provider = schemas.getInstance()
        this.setSchemasProvider(provider)
      }
      return schemasProvider
    }

    this.setEventBus = (bus) => { eventBus = bus }

    this.getEventBus = () => {
      if (!eventBus) {
        const bus = eventbus.getInstance(
          this.tenant,
          this.deviceGroup,
          this.environment,
          this.getIdManager(),
        )
        this.setEventBus(bus)
      }
      return eventBus
    }

    this.setIdManager = (manager) => { idManager = manager }

    this.getIdManager = () => {
      if (!idManager) {
        const manager = idmanager.getInstance()
        this.setIdManager(manager)
      }
      return idManager
    }

    this.isReady = () => {
      if (!schemasProvider) return false
      if (!idManager) return false
      return this.validator.isReady() && schemasProvider.isReady() && idManager.isReady()
    }

    window.addEventListener('beforeunload', () => { this.unload() })
  }

  /**
   * Sets a default content type.
   *
   * @param {string} contentType
   */
  useDefaultContentType(contentType) {
    this.defaultContentType = contentType
  }

  /**
   * Uses a custom {@link validator}-like object.
   *
   * @param {validator} customValidator
   */
  setValidator(customValidator) {
    this.validator = customValidator
  }

  /**
   * Sets current referer. Defaults to document.referrer.
   */
  setReferer(referer) {
    this.referer = referer || document.referrer
  }

  /**
   * Flush current queue.
   */
  unload() {
    this.flush()
  }

  /**
   * Returns the current scope information.
   *
   * @returns {object} Scope object containing url, actionTs and
   * horizonClientVersion.
   */
  getScopeInfo(relationId) {
    return {
      url: document.location.href,
      actionTs: +Date.now(),
      horizonClientVersion: this.clientVersion,
      horizonClientReferer: this.referer,
      horizonRelationId: relationId,
    }
  }

  /**
   * Validates the schema of the action object.
   *
   * @param {object} action Object containing user data
   */
  validateBeforeEnqueue(action) {
    const schema = this.getSchemasProvider().get(action.id, action.version)
    this.validator.validateFor(action, schema)
  }

  /**
   * Enqueues a callback to be executed when HorizonClient is ready.
   * This functions checks if both the {@link module:validator} and {@link SchemaProvider}
   * are ready. If not, it triggers the load function from both components.
   *
   * @param {function} callback function to be executed when the client is ready.
   * @see [Validator]{@link module:validator} for further information.
   * @see [SchemaProvider]{@link SchemasProvider} for schemas provider information.
   */
  onReady(callback) {
    if (!this.validator.isReady()) {
      // reschedule callback if tv4 has not loaded yet.

      this.state = horizonClientState.LOADING
      this.validator.onReady(callback)
      this.validator.load()
    } else if (!this.getSchemasProvider().isReady()) {
      // reschedule callback if schemas haven't loaded yet.

      this.state = horizonClientState.LOADING
      this.getSchemasProvider().onReady(callback)
      this.getSchemasProvider().load()
    } else if (!this.getIdManager().isReady()) {
      // reschedule callback if idManager haven't loaded yet.

      this.state = horizonClientState.LOADING
      this.getIdManager().onReady(callback)
      this.getIdManager().load()
    } else {
      // all components are ready to validate and dispatch
      // execute callback()

      this.state = horizonClientState.READY
      callback()
    }
  }

  /**
   * Flush the event queue when the client is ready.
   *
   */
  flush() {
    if (this.isReady()) {
      this.getEventBus().flush()
    } else {
      this.onReady(() => this.flush())
    }
  }

  /**
   * Enqueue an action or an array of actions to be send to horizon service.
   * First it makes some validation on each action structure.
   * Then calls `sendWithInfo` with the information of `getScopeInfo` in scope info param.
   * ready.
   * @see [Validator]{@link module:validator} for further information.
   * @param {(object|object[])} action an action or a list of actions representing user data.
   * @param {string} [userRelationId=null] optional unique identifier to associate with
   * user data enqueued.
   * @example <caption>Example of sending a single action without relationId.</caption>
   * client.send({
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor1',
   *      href: 'http://google.com'
   *    })
   * @example <caption>Example of sending a single action with relationId.</caption>
   * const relationId = uuidv4() // relationId = '1ade4ad1-2e7f-49d0-84f8-a00c5a22a33e'
   *   client.send({
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor1',
   *      href: 'http://google.com'
   *    }, relationId)
   * @example <caption>Example of sending multiple actions without relationId.</caption>
   *   client.send([{
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor1',
   *      href: 'http://google.com'
   *    },
   *    {
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor2',
   *      href: 'http://g1.globo.com'
   *    }])
   * @example <caption>Example of sending multiple actions with relationId.</caption>
   * const relationId = uuidv4() // relationId = '1ade4ad1-2e7f-49d0-84f8-a00c5a22a33e'
   *  client.send([{
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor1',
   *      href: 'http://google.com'
   *    },
   *    {
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor2',
   *      href: 'http://g1.globo.com'
   *    }], relationId)
   */
  send(actions, relationId = null) {
    if (relationId != null && typeof relationId !== 'string') {
      throw new Error(`${errors.INVALID_RELATION_ID} Wrong relationId type. It should be a String: actual type is ${typeof relationId}`)
    }
    const empty = []
    empty.concat(actions).forEach((action) => {
      this.validator.validateArgs(action)
      this.sendWithInfo(action, this.getScopeInfo(relationId))
    })
  }

  /**
   * Enqueue an action and an eventInfo to be send to horizon service.
   * It checks if the client is ready, if so enqueue the action on eventbus
   * queue, if not enqueue the send function to be executed when the client is
   * ready.
   * @param {object} action object containing user data
   * @param {object} scopeInfo object containing info about the scope
   * @example
   * client.sendWithInfo({
   *    id: 'generic-click',
   *    contentType: 'multicontent',
   *    version: "1.0",
   *    properties: {
   *      element: 'anchor1',
   *      href: 'http://google.com'
   *    },
   *    {
   *     url: 'http://google.com',
   *     actionTs: Date.now(),
   *     horizonClientVersion: "1.0",
   *     horizonClientReferer: "horizon-track",
   *    })
   */
  sendWithInfo(action, scopeInfo) {
    if (!this.isReady()) {
      if (this.state === horizonClientState.UNLOADED) {
        // first flush should be done asap after loading validator
        // and schema.
        this.flush()
      }

      // postpone send(action) if not ready.
      this.onReady(() => this.sendWithInfo(action, scopeInfo))
      return
    }

    this.validateBeforeEnqueue(action)
    const event = Object.assign({}, scopeInfo, action)

    // suport for old mode. Will be removed soon.
    if (!event.contentType) {
      if (!this.defaultContentType) {
        errors.mustBeDefined('contentType')
      }
      event.contentType = this.defaultContentType
    }

    this.getEventBus().enqueue(event)
  }

}

export default HorizonClient
